import useIsMobile from 'utils/hooks/useIsMobile'
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation'
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow'
import ListAltIcon from '@mui/icons-material/ListAlt'
import RotateLeftTwoToneIcon from '@mui/icons-material/RotateLeftTwoTone'
import SearchIcon from '@mui/icons-material/Search'
import {
  Backdrop,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import {DataGridPro, GridColDef, useGridApiRef} from '@mui/x-data-grid-pro'
import {DateRangePicker, LocalizationProvider} from '@mui/x-date-pickers-pro'
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs'
import {DateRange} from '@mui/x-date-pickers-pro/DateRangePicker'
import {yMarker} from 'assets/imageDeclear'
import dayjs, {Dayjs} from 'dayjs'
import {envBackHost, envImagePath, serverLocalName} from 'GLOBAL/envVariables'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import 'pages/common/print.css'
import FlaggedReviewValueChart from 'pages/customer/component/FlaggedReviewValueChart'
import {flaggedReviewImageStatusOptions} from 'pages/customer/dataConfig/declearColumns'
import {flaggedReviewTargetListRows} from 'pages/customer/dataConfig/declearRows'
import {targetImageLightBoxStyle} from 'pages/customer/dataConfig/LightBoxConfig'
import {CustomTag} from 'pages/customer/simpleComponent/CustomTag'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import {useSelector} from 'react-redux'
import {useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query'
import {fetchCustomerIssueCheckmeters} from 'utils/apiCalling/reactQueryApi'
import {useFlaggedReviewDetailExcel} from 'pages/customer/hooks/useFlaggedReviewExcelMutation'
import {
  globalConst,
  globalFormatWithHyphens,
  globalSerialNumberFormatWithHyphens,
  ImageProcessStatusEnum,
  processStatusButtonColor,
} from 'utils/const/globalConst'
import {showTemperatureHandler} from 'utils/const/globalController'
import {confirmAlert, resultAlert, validationAlert} from 'utils/CustomAlert/Alert'
import useMobileBackClose from 'utils/hooks/useMobileBackClose'
/*********************************************************************
 ** FlaggedReview 전용 상세 모달
 ** CustomerTargetImageListModal(pages/common)에서 분기용 조건이 계속 늘어나는 것을
 ** 막기 위해 FlaggedReview만을 위한 사본으로 분리함
 *********************************************************************/

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  height: '90%',
  bgcolor: 'background.paper',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  boxShadow: 24,
  p: {xs: 1, md: 4},
}

const FlaggedReviewDetailModal = (props: any) => {
  const isMobile = useIsMobile()
  const {data, open, onClose, mainDateRange} = props
  useMobileBackClose(open, onClose)
  const [date, setDate] = React.useState<DateRange<Dayjs>>([null, null])
  const [pageSize, setPageSize] = useState<number>(60)
  const [selectedOption, setSelectedOption] = useState<any>('reading')
  const officeInfo = useSelector((state: any) => state?.officeInfo)
  const userInfo = useSelector((state: any) => state?.userInfo?.userInfo)
  const defaultDetailFilter = {
    startDate: dayjs().subtract(7, 'day').format('YYYYMMDD'),
    endDate: dayjs().format('YYYYMMDD'),
  }
  // filter: 입력창 작업용 / appliedFilter: 실제 조회에 쓰는 확정 필터(queryKey)
  const [filter, setFilter] = useState<any>(defaultDetailFilter)
  const [appliedFilter, setAppliedFilter] = useState<any>(defaultDetailFilter)
  const [mobilePage, setMobilePage] = useState(0)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<any>('')
  const [rows, setRows] = useState<any[]>([])
  const [imageSizeCache, setImageSizeCache] = useState<
    Record<string, {width: number; height: number}>
  >({})
  const [flashRowId, setFlashRowId] = useState<any>(null)
  const [activeReviewFilters, setActiveReviewFilters] = useState<string[]>([])
  // 상세에서 검침값을 수정했는지 여부. 닫을 때 이 값이 true면 목록/칩 카운트를 재조회한다.
  const [isDirty, setIsDirty] = useState(false)
  // open 시 메인 기간으로 동기화가 끝났는지. 이 게이트 전에는 조회 쿼리를 켜지 않아 이중 조회를 막는다.
  const [detailReady, setDetailReady] = useState(false)

  const queryClient = useQueryClient()

  /*********************************************************************
   * react-query (상세 검침 이력)
   *********************************************************************/
  const {
    data: checkmeterList,
    isFetching: listFetching,
    refetch: refetchCheckmeters,
  }: UseQueryResult<any> = useQuery({
    queryKey: ['GetCustomerIssueCheckmeters', data?.officeId, data?.customerNumber, appliedFilter],
    queryFn: () =>
      fetchCustomerIssueCheckmeters(data?.officeId, data?.customerNumber, appliedFilter),
    // detailReady: open 시 메인 기간 동기화가 끝난 뒤에만 켬 → 옛 기간으로 나가는 1차 조회 방지
    enabled: open && detailReady && !!data?.officeId && !!data?.customerNumber,
    retry: false,
  })

  const loading = listFetching

  // 엑셀 다운로드: flagged-review 전용 엔드포인트(/customer-issue/checkmeters/excel) 직접 호출
  const {downloadDetail, isDetailDownloading} = useFlaggedReviewDetailExcel(
    data?.officeId,
    data?.customerNumber
  )

  const onCloseHandler = () => {
    setSelectedImage('')
    setIsZoomOpen(false)
    setRows([])
    setImageSizeCache({})
    setActiveReviewFilters([])
    // 모달을 닫을 때 해당 수용가 검침 이력 캐시를 stale 표시만 함(refetchType:'none').
    // 닫는 시점엔 쿼리가 아직 active라 그냥 invalidate하면 즉시 재조회가 트리거되어
    // "닫을 때 불필요한 호출"이 발생함. 재조회는 재오픈(enabled) 시점에 1번만 일어나게 한다.
    queryClient.invalidateQueries({
      queryKey: ['GetCustomerIssueCheckmeters', data?.officeId, data?.customerNumber],
      refetchType: 'none',
    })
    // 상세에서 값을 수정했을 때만 목록/칩 카운트를 재조회 → 고친 이슈가 목록·건수에 반영됨
    // (수정이 없었으면 불필요한 재조회·깜빡임을 피함)
    if (isDirty) {
      queryClient.invalidateQueries({queryKey: ['GetCustomerIssues']})
      queryClient.invalidateQueries({queryKey: ['GetCustomerIssueCounts']})
      setIsDirty(false)
    }
    onClose()
  }

  // 조회 결과를 rows에 반영 + 이미지 실제 크기 비동기 로딩(행 높이 계산용)
  // open을 의존성에 포함: 동일 수용가 재오픈 시(캐시 데이터 참조 불변) rows를 다시 채우기 위함
  useEffect(() => {
    if (!open) return
    // 데이터가 아직 없으면(수용가 전환 로딩 중) 이전 수용가 행이 남지 않도록 비움
    if (!checkmeterList) {
      setRows([])
      return
    }
    const list = checkmeterList
    const newRows = flaggedReviewTargetListRows(list).map((row: any) => {
      const cached = imageSizeCache[row.filePath]
      return cached
        ? {...row, imageWidth: cached.width, imageHeight: cached.height}
        : {...row, imageWidth: null, imageHeight: null}
    })
    setRows(newRows)
    setMobilePage(0)
    list.forEach((item: any) => {
      if (item.filePath && !imageSizeCache[item.filePath]) {
        const img = new Image()
        img.src = envImagePath + item.filePath
        img.onload = () => {
          const {width, height} = img
          setImageSizeCache((prev) => {
            const updated = {...prev, [item.filePath]: {width, height}}
            setRows((prevRows: any[]) =>
              prevRows.map((r) =>
                r.filePath === item.filePath ? {...r, imageWidth: width, imageHeight: height} : r
              )
            )
            return updated
          })
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkmeterList, open])

  const handleCellClick = (event: any) => {
    event.stopPropagation()
  }

  // 날짜 범위 입력 컨테이너 ref (플래시 애니메이션에서 시작일 입력을 찾기 위함)
  const dateRangeRef = useRef<HTMLDivElement>(null)

  // 시작일 입력창에 파란 테두리 플래시(연타해도 매번 재생되도록 Web Animations API 사용)
  const flashStartDate = () => {
    const startRoot = dateRangeRef.current?.querySelector(
      '.MuiInputBase-root'
    ) as HTMLElement | null
    startRoot?.animate(
      [
        {boxShadow: '0 0 0 0 rgba(29, 78, 216, 0.0)'},
        {boxShadow: '0 0 0 4px rgba(29, 78, 216, 0.9)', offset: 0.25},
        {boxShadow: '0 0 0 0 rgba(29, 78, 216, 0.0)'},
      ],
      {duration: 900, easing: 'ease-out'}
    )
  }

  // 종료일 기준으로 시작일을 지정 기간(예: 4일/1주일/1개월) 전으로 세팅하고 즉시 조회.
  // (세 기간 모두 1개월 이내라 별도 검증 없이 바로 appliedFilter에 반영)
  const setDetailDateQuickRange = (amount: number, unit: 'day' | 'week' | 'month') => {
    const end = date[1] ?? dayjs()
    const start = end.subtract(amount, unit)
    const next = {
      ...filter,
      startDate: start.format('YYYYMMDD'),
      endDate: end.format('YYYYMMDD'),
    }
    setDate([start, end])
    setFilter(next)
    setAppliedFilter(next) // 버튼 누르면 즉시 조회
    flashStartDate()
  }

  // 조회 버튼 클릭 시 기간이 최대 1개월인지 검증 후 조회 (초과 시 경고 후 차단)
  const handleSearch = () => {
    const {startDate, endDate} = filter
    if (!startDate || !endDate) {
      validationAlert('조회 기간을 선택해주세요.', 'warning')
      return
    }
    if (dayjs(endDate).isAfter(dayjs(startDate).add(1, 'month'), 'day')) {
      validationAlert('조회 기간을 최대 1개월 이내로 설정 부탁드립니다.', 'warning')
      return
    }
    setAppliedFilter({...filter})
  }

  // /customer-issue/checkmeters는 원본(checkValue)과 수정값(updateCheckValue)을 분리해서 내려줌.
  // 수정된 행은 updateCheckValue를 우선 표시해야 "인식불가"로 잘못 뜨는 것을 막을 수 있음.
  const resolveCheckValue = (row: any) => row?.updateCheckValue ?? row?.checkValue
  const resolveCheckDecimalValue = (row: any) =>
    row?.updateCheckDecimalValue ?? row?.checkDecimalValue

  const updateCheckValuePost = async (row: any, newValue: string) => {
    const serverHandle = async () => {
      if (['sys_admin', 'sys_user'].includes(userInfo?.accountType)) {
        return true
      } else {
        return await confirmAlert('수정', 'info')
      }
    }

    if (await serverHandle()) {
      try {
        const res = await axiosWithAuth().post(`${envBackHost}/checkmeter/update`, {
          idxDate: row.idxDate,
          value: newValue,
          imei: row.imei,
          updateId: userInfo?.userId,
        })

        await resultAlert(res.data)

        if (res.data.resultCode === 0) {
          // 수정 성공 → 상세 재조회 + dirty 표시(닫을 때 목록/칩 카운트 갱신 트리거)
          setIsDirty(true)
          await refetchCheckmeters()
        }
      } catch (e: any) {
        await resultAlert({resultCode: e.response?.status})
      }
    }
  }

  const accountTypeFixHandler = (type: string) => {
    switch (type) {
      case 'gov_meterman':
      case 'gov_coop':
        return false
      default:
        return true
    }
  }

  const getRowHeightOnDataGrid = (params: any) => {
    const {imageHeight} = params?.model || {}
    const heightMap: Record<number, number> = {
      480: 180,
      240: 160,
      144: 110,
    }

    if (imageHeight && heightMap[imageHeight]) {
      return heightMap[imageHeight]
    }
    return 110
  }

  const apiRef = useGridApiRef()
  const mobileListRef = useRef<HTMLDivElement>(null)

  // 비정상 사용량 / 검침값 확인 필요는 전후 검침값 비교로 유추해야 하므로, 필터링 시 인접 검침 행도 함께 표시
  const neighborRequiredFlags = ['METER_VALUE_DANGER', 'METER_VALUE_WARNING']

  const {filteredRows, contextRowIds} = useMemo(() => {
    if (activeReviewFilters.length === 0) {
      return {filteredRows: rows, contextRowIds: new Set<any>()}
    }

    const sortedByTime = [...rows].sort((a, b) =>
      String(a.idxDate).localeCompare(String(b.idxDate))
    )
    const indexById = new Map(sortedByTime.map((row, idx) => [row.id, idx]))

    const matchedIds = new Set<any>()
    const neighborIds = new Set<any>()

    rows.forEach((row) => {
      const flags: string[] = row.issueTypes ?? []
      const activeFlags = flags.filter((flag) => activeReviewFilters.includes(flag))
      if (activeFlags.length === 0) return

      matchedIds.add(row.id)

      if (activeFlags.some((flag) => neighborRequiredFlags.includes(flag))) {
        const idx = indexById.get(row.id)
        if (idx !== undefined) {
          if (sortedByTime[idx - 1]) neighborIds.add(sortedByTime[idx - 1].id)
          if (sortedByTime[idx + 1]) neighborIds.add(sortedByTime[idx + 1].id)
        }
      }
    })

    const contextOnlyIds = new Set(Array.from(neighborIds).filter((id) => !matchedIds.has(id)))

    return {
      filteredRows: rows.filter((row) => matchedIds.has(row.id) || neighborIds.has(row.id)),
      contextRowIds: contextOnlyIds,
    }
  }, [rows, activeReviewFilters])

  const reviewFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    flaggedReviewImageStatusOptions.forEach((option) => {
      counts[option.key] = rows.filter((row) => (row.issueTypes ?? []).includes(option.key)).length
    })
    return counts
  }, [rows])

  const toggleReviewFilter = (key: string) => {
    setActiveReviewFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
    setMobilePage(0)
  }

  const clearReviewFilter = () => {
    setActiveReviewFilters([])
    setMobilePage(0)
  }

  const handleChartRowClick = (row: any) => {
    const rowIndex = filteredRows.findIndex((r) => r.idxDate === row.idxDate)
    if (rowIndex === -1) return

    const rowId = filteredRows[rowIndex].id
    setFlashRowId(rowId)
    setTimeout(() => setFlashRowId((prev: any) => (prev === rowId ? null : prev)), 1200)

    if (isMobile) {
      setMobilePage(Math.floor(rowIndex / pageSize))
      mobileListRef.current && (mobileListRef.current.scrollTop = 0)
      return
    }

    const targetPage = Math.floor(rowIndex / pageSize)
    const currentPage = apiRef.current.state.pagination.page

    const scrollAndSelect = () => {
      apiRef.current.scrollToIndexes({rowIndex, colIndex: 0})
      apiRef.current.selectRow(rowId, true, true)
    }

    if (targetPage !== currentPage) {
      apiRef.current.setPage(targetPage)
      requestAnimationFrame(scrollAndSelect)
    } else {
      scrollAndSelect()
    }
  }

  useEffect(() => {
    if (!isMobile) {
      try {
        apiRef.current.setPage(0)
      } catch (e) {
        // grid not mounted yet
      }
    }
  }, [activeReviewFilters, isMobile])

  useEffect(() => {
    const today = dayjs()
    const oneWeekAgo = today.subtract(7, 'day')
    setDate([oneWeekAgo, today])
  }, [])

  // 상세 진입 시 메인에서 선택한 이슈 조회 기간으로 초기화(동기화).
  // 메인 기간이 없으면 기본값(1주일)로. 모달 안 리셋 버튼은 1주일로 되돌린다.
  // setDetailReady(true)를 동기화와 같은 배치에서 처리 → 재렌더 1회로 올바른 기간에 조회 1번만 발생.
  useEffect(() => {
    if (!open) {
      // 닫히면 게이트를 내려 다음 오픈 때 다시 동기화 후 조회하도록 함
      setDetailReady(false)
      return
    }
    // YYYYMMDD 문자열 → dayjs (플러그인 없이 안전하게 ISO 형태로 변환)
    const toDayjs = (v: string) =>
      dayjs(`${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`)
    const start = mainDateRange?.startDate ?? defaultDetailFilter.startDate
    const end = mainDateRange?.endDate ?? defaultDetailFilter.endDate
    const nextFilter = {startDate: start, endDate: end}
    setFilter(nextFilter)
    setAppliedFilter(nextFilter)
    setDate([toDayjs(start), toDayjs(end)])
    setDetailReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (open && data && data.latitude && data.longitude) {
      setTimeout(() => {
        let container = document?.getElementById('map')
        if (container) {
          const map = new window.kakao.maps.Map(container, {
            center: new window.kakao.maps.LatLng(data.latitude, data.longitude),
            mapTypeId: window.kakao.maps.MapTypeId.HYBRID,
            level: 1,
            draggable: false,
            scrollwheel: false,
            zoomable: false,
          })
          const mapTypeControl = new window.kakao.maps.MapTypeControl()
          map.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT)
          var markerPosition = new window.kakao.maps.LatLng(data.latitude, data.longitude)

          var marker = new window.kakao.maps.Marker({
            position: markerPosition,
            image: new window.kakao.maps.MarkerImage(yMarker, new window.kakao.maps.Size(40, 59)),
          })

          marker.setMap(map)
        }
      }, 300)
    }
  }, [open])

  const columns: GridColDef[] = [
    {
      field: 'createAt',
      headerName: '검침일시',
      width: 260,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        return <div>{dayjs(params?.row?.createAt).format('YYYY-MM-DD HH시 mm분 ss초')}</div>
      },
    },
    {
      field: 'filePath',
      headerName: '검침 이미지',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      hide: data?.deviceType === 'digital',
      renderCell: (params: any) => {
        return (
          <div
            className='cursor-zoom-in'
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              onClick={() => {
                setIsZoomOpen(true)
                setSelectedImage(`${envImagePath + params?.row?.filePath}`)
              }}
              src={envImagePath + params?.row?.filePath}
              alt='check'
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `rotate(${params?.row?.rotate}deg)`,
              }}
            />
          </div>
        )
      },
    },
    {
      field: 'reviewFlags',
      headerName: '확인 필요 사유',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const reviewFlags: string[] = params.row.issueTypes ?? []
        const activeStatuses = flaggedReviewImageStatusOptions.filter((option) =>
          reviewFlags.includes(option.key)
        )
        const isContextRow = contextRowIds.has(params.row.id)
        return (
          <div className='flex flex-wrap items-center justify-center gap-1'>
            {activeStatuses.map((status) => (
              <CustomTag
                key={status.key}
                label={status.label}
                className={`text-base px-3 py-3 ${status.badgeClassName}`}
              />
            ))}
            {isContextRow && (
              <CustomTag
                label='비교용'
                className='text-sm px-2 py-1 bg-gray-100 text-gray-500 border border-gray-300'
              />
            )}
          </div>
        )
      },
    },

    {
      field: 'processStatus',
      headerName: '이미지 처리상태',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const status = params?.row?.processStatus
        const enumStatus = ImageProcessStatusEnum[status as keyof typeof ImageProcessStatusEnum]
        return <span>{status === 'digital' ? '디지털 계량기' : enumStatus}</span>
      },
    },
    {
      field: 'checkValue',
      headerName: '검침 값',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const checkValue = resolveCheckValue(params.row)
        const checkDecimalValue = resolveCheckDecimalValue(params.row)
        return (
          <>
            <div className={'d-flex justify-content-center'}>
              <span>
                {checkValue === '' || checkValue === null ? (
                  <span className={'text-danger'}>인식불가</span>
                ) : (
                  checkValue
                )}
              </span>
              <span>
                {checkDecimalValue === '' || checkDecimalValue === null
                  ? ''
                  : '.' + checkDecimalValue?.split('.')[1]}
              </span>
            </div>
          </>
        )
      },
    },
    {
      field: 'valueUpdate',
      headerName: '검침값 수정',
      width: 280,
      align: 'center',
      headerAlign: 'center',
      hide: data?.deviceType === 'digital' || data?.recognizedType === 'PRV',
      renderCell: (params: any) => {
        const rowKey = `${params.row.imei}_${params.row.idxDate}`
        let localInput: HTMLInputElement | null = null
        return (
          <div className='d-flex justify-content-center gap-3' onClick={(e) => handleCellClick(e)}>
            <TextField
              sx={{textAlignLast: 'center'}}
              variant='outlined'
              size='small'
              defaultValue={resolveCheckValue(params.row)}
              inputRef={(el) => (localInput = el)}
              autoComplete='off'
              inputProps={{inputMode: 'numeric'}}
              // 숫자만 허용(빈값 허용). 붙여넣기·조합입력 포함 비숫자 문자를 즉시 제거
              onInput={(e) => {
                const t = e.target as HTMLInputElement
                t.value = t.value.replace(/[^0-9]/g, '')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newVal = localInput?.value || ''
                  updateCheckValuePost(params.row, newVal)
                }
                if (e.ctrlKey && e.key === 'a') e.stopPropagation()
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.stopPropagation()
                }
              }}
              disabled={
                !accountTypeFixHandler(userInfo?.accountType) ||
                params.row.processStatus === 'IMGERR'
              }
            />

            {accountTypeFixHandler(userInfo?.accountType) && (
              <Button
                variant='contained'
                color={processStatusButtonColor(params.row.processStatus)}
                size='small'
                onClick={() => {
                  const newVal = localInput?.value || ''
                  updateCheckValuePost(params.row, newVal)
                }}
                disabled={params.row.processStatus === 'IMGERR'}
              >
                수정
              </Button>
            )}
          </div>
        )
      },
    },
    {
      field: 'temperature',
      headerName: '온도',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const temperature = params.row.temperature
        return (
          <>
            <div className={'d-flex justify-content-center'}>
              {temperature !== 99 && showTemperatureHandler(serverLocalName, officeInfo.value)
                ? `${temperature}°C`
                : '-'}
            </div>
          </>
        )
      },
    },

    {
      // 전파세기·전파품질을 한 셀로 합쳐 가로 스크롤을 줄임 (세기 / 품질)
      field: 'signalStrength',
      headerName: '전파세기/품질',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: any) => {
        const signalStrength = params.row.signalStrength
        const linkquality = params.row.linkquality
        const isWeakSignal = signalStrength !== null && signalStrength < globalConst.rssiWarningdBm
        const isWeakQuality = linkquality !== null && linkquality < globalConst.rsrqGood

        return (
          <div className='d-flex justify-content-center align-items-center' style={{gap: 4}}>
            <span className={isWeakSignal ? 'text-orange-500 fw-bold' : ''}>
              {signalStrength !== null ? `${signalStrength}dbm` : '-'}
            </span>
            <span className='text-gray-400'>/</span>
            <span className={isWeakQuality ? 'text-orange-500 fw-bold' : ''}>
              {linkquality !== null ? `${linkquality}dB` : '-'}
            </span>
          </div>
        )
      },
    },
  ]
  const customerFields = [
    {label: '수용가번호', value: globalFormatWithHyphens(data?.officeId, data?.customerNumber)},
    {label: '수용가 이름', value: data?.customerName},
    {label: '수용가 주소', value: data?.addressRoad},
    {label: '계량기 번호', value: data?.meterNumber},
    {label: '계량기 구경', value: data?.meterDiameter + 'mm'},
    {label: '단말기 IMEI', value: data?.imei},
    {label: '단말기 SN', value: globalSerialNumberFormatWithHyphens(data?.serialNumber)},
    {
      label: '설치|A/S 전파세기',
      value: data?.completeSignalStrength != null ? `${data.completeSignalStrength}dbm` : '-',
    },
    {
      label: '설치|A/S 전파품질',
      value: data?.completeLinkquality != null ? `${data.completeLinkquality}dB` : '-',
    },
  ]

  return (
    <Modal
      open={open}
      onClose={() => {
        onCloseHandler()
      }}
      aria-labelledby={`${window.location.pathname}-label`}
      aria-describedby={`${window.location.pathname}-description`}
      closeAfterTransition
      slots={{backdrop: Backdrop}}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      id={'printTarget'}
      className={'printTarget'}
    >
      <>
        <style>{`
          @keyframes flaggedReviewCardFlash {
            0% { background-color: rgba(37, 99, 235, 0.35); }
            100% { background-color: transparent; }
          }
          .flagged-review-card-flash {
            animation: flaggedReviewCardFlash 1.2s ease-out;
          }
        `}</style>
        <Box sx={style}>
          <div className='flex flex-col h-full min-h-0'>
            <div className='flex justify-between items-center mb-2'>
              <Chip
                label={`${data?.imei} 상세정보`}
                variant={'outlined'}
                sx={{fontSize: isMobile ? '14px' : '20px', fontWeight: 'bold'}}
              />
              <CancelPresentationIcon
                sx={{fontSize: '40px', cursor: 'pointer'}}
                color={'error'}
                onClick={() => {
                  onCloseHandler()
                }}
              />
            </div>

            <div className='flex-1 min-h-0 overflow-y-auto lg:overflow-hidden'>
              {data && (
                <div className='flex flex-col lg:flex-row gap-4 lg:h-full'>
                  {/* 왼쪽: 수용가 정보 */}
                  <div className='w-full lg:w-1/4 min-w-0 flex flex-col lg:h-full'>
                    <div className='border border-1 border-gray-400 shadow-sm px-0 flex flex-col flex-1 min-h-0'>
                      <Paper className='p-4 border-bottom border-1 border-gray-400' square>
                        <span className='font-semibold text-[1.25rem]'>수용가 정보</span>
                      </Paper>
                      <Paper
                        className='p-4'
                        square
                        style={{flex: 1, overflow: 'auto', minHeight: 0}}
                        sx={!isMobile ? {'& .MuiInputBase-root': {height: 48}} : {}}
                      >
                        <div className='flex flex-col gap-2'>
                          {customerFields.map(({label, value}) => (
                            <div key={label} className='flex flex-col gap-1'>
                              <span className='flex items-center gap-1 font-semibold text-[1rem]'>
                                <DoubleArrowIcon /> {label}
                              </span>
                              <TextField
                                variant={'outlined'}
                                size='small'
                                className='bg-gray-300'
                                value={value}
                                inputProps={{readOnly: true}}
                              />
                            </div>
                          ))}
                        </div>
                      </Paper>
                    </div>
                    <div
                      id='map'
                      style={{width: '100%', height: 250}}
                      className='rounded-2 border-gray-400 border border-1 mt-2'
                    />
                  </div>

                  {/* 오른쪽: 날짜 + 차트 + 테이블 */}
                  <div className='flex-1 min-w-0 flex flex-col gap-2 lg:h-full lg:min-h-0'>
                    <div
                      className={`flex flex-wrap gap-2 items-center ${isMobile ? 'py-1' : 'py-3'}`}
                    >
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{start: '시작 날짜', end: '마지막 날짜'}}
                      >
                        <DateRangePicker
                          PopperProps={{style: {zIndex: 9999}}}
                          value={date}
                          calendars={3}
                          mask='____/__/__'
                          inputFormat={'YYYY/MM/DD'}
                          onChange={(newValue) => {
                            setDate(newValue)
                            setFilter({
                              ...filter,
                              startDate: newValue[0]?.format('YYYYMMDD'),
                              endDate: newValue[1]?.format('YYYYMMDD'),
                            })
                          }}
                          renderInput={(startProps, endProps) => (
                            <div className='flex items-center gap-2' ref={dateRangeRef}>
                              <TextField
                                {...startProps}
                                size='small'
                                autoComplete='off'
                                InputLabelProps={{
                                  shrink: true,
                                  style: {fontSize: isMobile ? '1.2rem' : '1.5rem'},
                                }}
                                InputProps={{
                                  ...startProps.InputProps,
                                  sx: {
                                    height: isMobile ? 36 : 48,
                                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                                  },
                                }}
                              />
                              <span className='text-gray-500 font-semibold'>~</span>
                              <TextField
                                {...endProps}
                                size='small'
                                autoComplete='off'
                                InputLabelProps={{
                                  shrink: true,
                                  style: {fontSize: isMobile ? '1.2rem' : '1.5rem'},
                                }}
                                InputProps={{
                                  ...endProps.InputProps,
                                  sx: {
                                    height: isMobile ? 36 : 48,
                                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                                  },
                                }}
                              />
                            </div>
                          )}
                        />
                      </LocalizationProvider>
                      <Tooltip title='종료일 기준 4일 (동일 이미지 확인용)' arrow>
                        <Button
                          variant={'outlined'}
                          sx={{height: isMobile ? 36 : 48, textTransform: 'none'}}
                          onClick={() => setDetailDateQuickRange(4, 'day')}
                        >
                          4일
                        </Button>
                      </Tooltip>
                      <Tooltip title='종료일 기준 1주일' arrow>
                        <Button
                          variant={'outlined'}
                          sx={{height: isMobile ? 36 : 48, textTransform: 'none'}}
                          onClick={() => setDetailDateQuickRange(1, 'week')}
                        >
                          1주일
                        </Button>
                      </Tooltip>
                      <Tooltip title='종료일 기준 1개월' arrow>
                        <Button
                          variant={'outlined'}
                          sx={{height: isMobile ? 36 : 48, textTransform: 'none'}}
                          onClick={() => setDetailDateQuickRange(1, 'month')}
                        >
                          1개월
                        </Button>
                      </Tooltip>
                      <Button
                        variant={'contained'}
                        color={'error'}
                        sx={{height: isMobile ? 36 : 48}}
                        onClick={() => {
                          const today = dayjs()
                          const oneWeekAgo = today.subtract(7, 'day')
                          const resetFilter = {
                            ...filter,
                            startDate: oneWeekAgo.format('YYYYMMDD'),
                            endDate: today.format('YYYYMMDD'),
                          }
                          setFilter(resetFilter)
                          setDate([oneWeekAgo, today])
                          setAppliedFilter(resetFilter)
                        }}
                      >
                        <RotateLeftTwoToneIcon
                          sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}}
                        />
                      </Button>
                      <FormControl
                        sx={{minWidth: isMobile ? 100 : 120, height: isMobile ? 36 : 48}}
                        size='small'
                      >
                        {!isMobile && <InputLabel id='select-label'>기준 선택</InputLabel>}
                        <Select
                          labelId='select-label'
                          id='select'
                          value={selectedOption}
                          label={isMobile ? undefined : '기준 선택'}
                          onChange={(e) => setSelectedOption(e.target.value)}
                          sx={{height: isMobile ? 36 : 48}}
                        >
                          <MenuItem value='reading'>검침값</MenuItem>
                          <MenuItem value='usage'>사용량</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant={'contained'}
                        sx={{height: isMobile ? 36 : 48}}
                        onClick={() => handleSearch()}
                      >
                        <SearchIcon sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}} />
                      </Button>
                      <Button
                        variant={'outlined'}
                        color={'success'}
                        startIcon={
                          <ListAltIcon
                            sx={{width: isMobile ? 18 : 24, height: isMobile ? 18 : 24}}
                          />
                        }
                        sx={{height: isMobile ? 36 : 48, textTransform: 'none'}}
                        disabled={isDetailDownloading}
                        onClick={() => downloadDetail(appliedFilter)}
                      >
                        {isMobile ? 'Excel 다운' : 'Download'}
                      </Button>
                    </div>

                    <div
                      style={{
                        height: isMobile ? '240px' : '230px',
                        flexShrink: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {checkmeterList && (
                        <FlaggedReviewValueChart
                          rows={filteredRows}
                          selectedOption={selectedOption}
                          loading={loading}
                          chartHeight={isMobile ? 200 : 190}
                          onRowClick={handleChartRowClick}
                        />
                      )}
                    </div>

                    <div className='flex flex-wrap items-center gap-2 py-1'>
                      <button
                        type='button'
                        onClick={clearReviewFilter}
                        className={`flex items-center gap-1 px-3 py-2 rounded-full text-base font-semibold shadow-sm transition ${
                          activeReviewFilters.length === 0
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        전체 {rows.length}
                      </button>
                      {flaggedReviewImageStatusOptions.map(({key, label, dotClassName}) => {
                        const isActive = activeReviewFilters.includes(key)
                        return (
                          <button
                            key={key}
                            type='button'
                            onClick={() => toggleReviewFilter(key)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-base font-semibold border-2 shadow-sm transition ${
                              isActive
                                ? 'bg-white border-gray-900 text-gray-900'
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full ${dotClassName}`} />
                            {label}
                            <span className='text-gray-400 font-normal'>
                              {reviewFilterCounts[key]}
                            </span>
                          </button>
                        )
                      })}
                    </div>

                    {isMobile ? (
                      <div className='flex flex-col' style={{height: '65vh'}}>
                        <div
                          ref={mobileListRef}
                          className='flex-1 overflow-y-auto flex flex-col gap-2'
                        >
                          {loading ? (
                            <div className='flex justify-center p-8'>
                              <span>로딩 중...</span>
                            </div>
                          ) : (
                            filteredRows
                              .slice(mobilePage * pageSize, (mobilePage + 1) * pageSize)
                              .map((row) => {
                                let localInput: HTMLInputElement | null = null
                                const processLabel =
                                  row.processStatus === 'digital'
                                    ? '디지털 계량기'
                                    : ImageProcessStatusEnum[
                                        row.processStatus as keyof typeof ImageProcessStatusEnum
                                      ]
                                const isWeakSignal =
                                  row.signalStrength !== null &&
                                  row.signalStrength < globalConst.rssiWarningdBm
                                const isWeakQuality =
                                  row.linkquality !== null && row.linkquality < globalConst.rsrqGood
                                const activeStatuses = flaggedReviewImageStatusOptions.filter(
                                  (option) => (row.issueTypes ?? []).includes(option.key)
                                )
                                return (
                                  <div
                                    key={row.id}
                                    className={`border border-gray-200 rounded-lg p-3 bg-white shadow-sm flex gap-3 ${
                                      row.id === flashRowId ? 'flagged-review-card-flash' : ''
                                    }`}
                                  >
                                    {/* 왼쪽: 텍스트 정보 */}
                                    <div className='flex flex-col gap-1 flex-1 min-w-0'>
                                      <div className='font-bold text-gray-800'>
                                        <div className='text-xl'>
                                          {dayjs(row.createAt).format('YY/MM/DD')}
                                        </div>
                                        <div className='text-xl'>
                                          {dayjs(row.createAt).format('HH시 mm분 ss초')}
                                        </div>
                                      </div>
                                      {(activeStatuses.length > 0 || contextRowIds.has(row.id)) && (
                                        <div className='flex flex-wrap gap-1'>
                                          {activeStatuses.map((status) => (
                                            <CustomTag
                                              key={status.key}
                                              label={status.label}
                                              className={`text-sm px-2 py-1 ${status.badgeClassName}`}
                                            />
                                          ))}
                                          {contextRowIds.has(row.id) && (
                                            <CustomTag
                                              label='비교용'
                                              className='text-sm px-2 py-1 bg-gray-100 text-gray-500 border border-gray-300'
                                            />
                                          )}
                                        </div>
                                      )}
                                      <div className='flex items-center gap-2'>
                                        <span className='text-lg text-gray-600'>처리상태</span>
                                        <span className='text-xl text-gray-800'>
                                          {processLabel}
                                        </span>
                                      </div>
                                      <div className='flex items-center gap-2'>
                                        <span className='text-lg text-gray-600'>검침값</span>
                                        <span className='text-2xl font-bold'>
                                          {resolveCheckValue(row) === '' ||
                                          resolveCheckValue(row) === null ? (
                                            <span className='text-red-500'>인식불가</span>
                                          ) : (
                                            resolveCheckValue(row)
                                          )}
                                          {resolveCheckDecimalValue(row)
                                            ? '.' + resolveCheckDecimalValue(row).split('.')[1]
                                            : ''}
                                        </span>
                                      </div>
                                      <div className='flex gap-3 flex-wrap mt-1'>
                                        {showTemperatureHandler(
                                          serverLocalName,
                                          officeInfo.value
                                        ) &&
                                          row.temperature !== 99 && (
                                            <span className='text-lg text-gray-500'>
                                              {row.temperature}°C
                                            </span>
                                          )}
                                        <div className='flex items-center gap-2'>
                                          <span className='text-lg text-gray-600'>전파세기</span>
                                          <span
                                            className={`text-lg ${
                                              isWeakSignal
                                                ? 'text-orange-500 font-bold'
                                                : 'text-gray-800'
                                            }`}
                                          >
                                            {row.signalStrength !== null
                                              ? `${row.signalStrength}dbm`
                                              : '-'}
                                          </span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                          <span className='text-lg text-gray-600'>전파품질</span>
                                          <span
                                            className={`text-lg ${
                                              isWeakQuality
                                                ? 'text-orange-500 font-bold'
                                                : 'text-gray-800'
                                            }`}
                                          >
                                            {row.linkquality !== null
                                              ? `${row.linkquality}dB`
                                              : '-'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    {/* 오른쪽: 이미지 + 수정 */}
                                    <div
                                      className='shrink-0 flex flex-col gap-2 items-center'
                                      style={{width: '50%'}}
                                    >
                                      {row.filePath && data?.deviceType !== 'digital' && (
                                        <img
                                          src={envImagePath + row.filePath}
                                          alt='check'
                                          className='w-full object-contain rounded cursor-zoom-in border border-gray-200'
                                          style={{
                                            maxHeight: 100,
                                            transform: `rotate(${
                                              (row.rotate > 90 && row.rotate < 270) ||
                                              (row.rotate < -90 && row.rotate > -270)
                                                ? 180
                                                : 0
                                            }deg)`,
                                          }}
                                          onClick={() => {
                                            setIsZoomOpen(true)
                                            setSelectedImage(envImagePath + row.filePath)
                                          }}
                                        />
                                      )}
                                      {data?.deviceType !== 'digital' &&
                                        data?.recognizedType !== 'PRV' && (
                                          <div
                                            className='flex flex-row gap-1 w-full items-stretch'
                                            onClick={(e) => handleCellClick(e)}
                                          >
                                            <TextField
                                              variant='outlined'
                                              size='small'
                                              defaultValue={resolveCheckValue(row)}
                                              inputRef={(el) => (localInput = el)}
                                              autoComplete='off'
                                              inputProps={{inputMode: 'numeric'}}
                                              // 숫자만 허용(빈값 허용). 비숫자 문자를 즉시 제거
                                              onInput={(e) => {
                                                const t = e.target as HTMLInputElement
                                                t.value = t.value.replace(/[^0-9]/g, '')
                                              }}
                                              disabled={
                                                !accountTypeFixHandler(userInfo?.accountType) ||
                                                row.processStatus === 'IMGERR'
                                              }
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter')
                                                  updateCheckValuePost(row, localInput?.value || '')
                                              }}
                                              sx={{flex: 1, '& .MuiInputBase-root': {height: 40}}}
                                            />
                                            {accountTypeFixHandler(userInfo?.accountType) && (
                                              <Button
                                                variant='contained'
                                                color={processStatusButtonColor(row.processStatus)}
                                                disabled={row.processStatus === 'IMGERR'}
                                                sx={{height: 40, minWidth: 0, px: 1.5}}
                                                onClick={() =>
                                                  updateCheckValuePost(row, localInput?.value || '')
                                                }
                                              >
                                                수정
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )
                              })
                          )}
                        </div>
                        {/* 페이지네이션 */}
                        <div className='flex items-center justify-between border-t pt-2 mt-1 shrink-0'>
                          <FormControl size='small' sx={{minWidth: 80}}>
                            <Select
                              value={pageSize}
                              onChange={(e) => {
                                setPageSize(Number(e.target.value))
                                setMobilePage(0)
                              }}
                            >
                              {[10, 20, 60, 100].map((s) => (
                                <MenuItem key={s} value={s}>
                                  {s}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span className='text-sm text-gray-600'>
                            {mobilePage * pageSize + 1}–
                            {Math.min((mobilePage + 1) * pageSize, filteredRows.length)} /{' '}
                            {filteredRows.length}
                          </span>
                          <div className='flex gap-1'>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={mobilePage === 0}
                              onClick={() => {
                                setMobilePage((p) => p - 1)
                                mobileListRef.current && (mobileListRef.current.scrollTop = 0)
                              }}
                            >
                              ‹
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={(mobilePage + 1) * pageSize >= filteredRows.length}
                              onClick={() => {
                                setMobilePage((p) => p + 1)
                                mobileListRef.current && (mobileListRef.current.scrollTop = 0)
                              }}
                            >
                              ›
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Box id='table-content' sx={{flex: 1, minHeight: 0, width: '100%'}}>
                        <DataGridPro
                          rows={filteredRows}
                          columns={columns}
                          rowHeight={110}
                          getRowHeight={(params: any) => getRowHeightOnDataGrid(params)}
                          getRowClassName={(params) =>
                            params.id === flashRowId ? 'flagged-review-row-flash' : ''
                          }
                          apiRef={apiRef}
                          pageSize={pageSize}
                          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                          rowsPerPageOptions={[10, 20, 60, 100]}
                          disableColumnMenu
                          pagination
                          loading={loading}
                          sx={{
                            '@keyframes flaggedReviewRowFlash': {
                              '0%': {backgroundColor: 'rgba(37, 99, 235, 0.35)'},
                              '100%': {backgroundColor: 'transparent'},
                            },
                            '& .flagged-review-row-flash': {
                              animation: 'flaggedReviewRowFlash 1.2s ease-out',
                            },
                          }}
                        />
                      </Box>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Box>
        {isZoomOpen && selectedImage && (
          <Lightbox
            mainSrc={selectedImage}
            wrapperClassName='target-image-light-box'
            reactModalStyle={targetImageLightBoxStyle(isMobile)}
            onCloseRequest={() => {
              setIsZoomOpen(false)
              setSelectedImage('')
            }}
          />
        )}
      </>
    </Modal>
  )
}
export default FlaggedReviewDetailModal
